import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"
import { hasOwnHeight, hasOwnTextSize, hasOwnWidth, isPositioned, touchHitArea } from "@/components/ui/touch-target"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

/**
 * Phone-only sizing (max-md:), so desktop is unchanged. Buttons the caller
 * hasn't sized grow to a 44px touch target; anything that stays smaller
 * (size="sm", h-7, an absolute icon button) gets an invisible 44px hit area.
 */
function buttonTouchClasses(
  variant: ButtonProps["variant"],
  size: ButtonProps["size"],
  own: string
) {
  // Inline text links keep their size; a hit area would cover nearby text.
  if (variant === "link") return ""
  const ownHeight = hasOwnHeight(own)
  let grown = false
  let classes = ""
  if (size === "icon") {
    if (!ownHeight && !hasOwnWidth(own) && !isPositioned(own)) {
      grown = true
      classes = /\[&_svg\]:size-/.test(own)
        ? "max-md:h-11 max-md:w-11"
        : "max-md:h-11 max-md:w-11 max-md:[&_svg]:size-5"
    }
  } else if (size === "sm") {
    classes = cn(!ownHeight && "max-md:h-9", !hasOwnTextSize(own) && "max-md:text-[13px]")
  } else if (!ownHeight) {
    grown = true
    classes = "max-md:h-11"
  }
  return grown ? classes : cn(classes, touchHitArea(own))
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    // With asChild the child's own classes size and position it too.
    const childClassName =
      asChild && React.isValidElement<{ className?: string }>(props.children)
        ? props.children.props.className
        : undefined
    const own = childClassName ? `${className ?? ""} ${childClassName}` : className ?? ""
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size }),
          buttonTouchClasses(variant, size, own),
          className
        )}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
